import { db } from "../firebaseClient"
import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  collection,
  getDocs,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore"
import { generateLocalId } from "./courseHelpers"
import { listCourseEnrollments } from "./enrollmentHelpers"
import { toMillis } from "./dateRangeHelpers"
import { ACTIVE_WITHIN_DAYS } from "./learnerHealthHelpers"

const DAY_MS = 86400000

export const createCohort = async ({ orgId, name, courseId, description }) => {
  if (!orgId || !name?.trim() || !courseId) {
    return { cohortId: null, error: { message: "Name and course are required" } }
  }
  try {
    const cohortRef = doc(collection(db, "organizations", orgId, "cohorts"))
    await setDoc(cohortRef, {
      name: name.trim(),
      courseId,
      description: description || "",
      status: "active",
      startDate: null,
      endDate: null,
      scheduleEvents: [],
      announcements: [],
      attendance: {},
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return { cohortId: cohortRef.id, error: null }
  } catch (error) {
    return { cohortId: null, error }
  }
}

export const updateCohort = async (orgId, cohortId, updates) => {
  try {
    await updateDoc(doc(db, "organizations", orgId, "cohorts", cohortId), {
      ...updates,
      updatedAt: serverTimestamp(),
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const deleteCohort = async (orgId, cohortId) => {
  try {
    await deleteDoc(doc(db, "organizations", orgId, "cohorts", cohortId))
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const getCohort = async (orgId, cohortId) => {
  try {
    const snap = await getDoc(doc(db, "organizations", orgId, "cohorts", cohortId))
    if (!snap.exists()) return { cohort: null, error: null }
    return { cohort: { cohortId: snap.id, ...snap.data() }, error: null }
  } catch (error) {
    return { cohort: null, error }
  }
}

export const listCohorts = async (orgId) => {
  try {
    const snap = await getDocs(collection(db, "organizations", orgId, "cohorts"))
    return { cohorts: snap.docs?.map((d) => ({ cohortId: d.id, ...d.data() })), error: null }
  } catch (error) {
    return { cohorts: [], error }
  }
}

// Learners in a cohort = enrollments in the cohort's course, filtered down
// to this cohortId. Deliberately not a separate query — cohortId lives on
// the enrollment doc (see assignCohort in enrollmentHelpers.js), same
// pattern as everything else that avoids a dedicated join collection here.
export const getCohortLearners = async (orgId, cohortId, courseId) => {
  const { enrollments } = await listCourseEnrollments(orgId, courseId)
  return { enrollments: enrollments?.filter((e) => e?.cohortId === cohortId) || [], error: null }
}

export const computeCohortAssessmentPerformance = async (orgId, cohort) => {
  const { enrollments } = await getCohortLearners(orgId, cohort?.cohortId, cohort?.courseId)
  const attempts = []
  enrollments?.forEach((e) => {
    Object.values(e?.moduleQuizResults || {})?.forEach((r) => {
      if (r?.attemptCount) attempts.push({ score: r?.bestScore || 0, passed: !!r?.passed })
    })
    if (e?.capstoneResult?.attemptCount) attempts.push({ score: e?.capstoneResult?.bestScore || 0, passed: !!e?.capstoneResult?.passed })
  })
  const totalCompleted = attempts?.length
  const avgScore = totalCompleted ? Math.round(attempts.reduce((s, a) => s + a?.score, 0) / totalCompleted) : 0
  const passCount = attempts?.filter((a) => a?.passed)?.length || 0
  const passRate = totalCompleted ? Math.round((passCount / totalCompleted) * 1000) / 10 : 0
  return { totalCompleted, avgScore, passRate }
}

export const computeCohortSummary = async (orgId, cohort) => {
  const { enrollments } = await getCohortLearners(orgId, cohort?.cohortId, cohort?.courseId)
  const total = enrollments?.length || 0
  const now = Date.now()

  const activeCount =
    enrollments?.filter((e) => {
      const ms = toMillis(e?.lastActivityAt)
      return ms !== null && (now - ms) / DAY_MS <= ACTIVE_WITHIN_DAYS
    })?.length || 0

  const completedCount = enrollments?.filter((e) => (e?.progressPercent || 0) >= 100)?.length || 0

  return {
    total,
    activePercent: total ? Math.round((activeCount / total) * 100) : 0,
    completionPercent: total ? Math.round((completedCount / total) * 100) : 0,
  }
}

export const addScheduleEvent = async (orgId, cohortId, event) => {
  try {
    await updateDoc(doc(db, "organizations", orgId, "cohorts", cohortId), {
      scheduleEvents: arrayUnion({ id: generateLocalId(), ...event }),
      updatedAt: serverTimestamp(),
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// arrayUnion can only add — editing or removing a specific event needs a
// read-modify-write against the current array instead.
export const updateScheduleEvent = async (orgId, cohortId, eventId, updates) => {
  try {
    const ref = doc(db, "organizations", orgId, "cohorts", cohortId)
    const snap = await getDoc(ref)
    if (!snap.exists()) return { error: { message: "Cohort not found" } }
    const events = snap.data()?.scheduleEvents || []
    const nextEvents = events.map((e) => (e.id === eventId ? { ...e, ...updates } : e))
    await updateDoc(ref, { scheduleEvents: nextEvents, updatedAt: serverTimestamp() })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const removeScheduleEvent = async (orgId, cohortId, eventId) => {
  try {
    const ref = doc(db, "organizations", orgId, "cohorts", cohortId)
    const snap = await getDoc(ref)
    if (!snap.exists()) return { error: { message: "Cohort not found" } }
    const events = snap.data()?.scheduleEvents || []
    const nextEvents = events.filter((e) => e.id !== eventId)
    await updateDoc(ref, { scheduleEvents: nextEvents, updatedAt: serverTimestamp() })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const postAnnouncement = async (orgId, cohortId, announcement) => {
  try {
    await updateDoc(doc(db, "organizations", orgId, "cohorts", cohortId), {
      announcements: arrayUnion({ id: generateLocalId(), postedAt: new Date().toISOString(), ...announcement }),
      updatedAt: serverTimestamp(),
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const removeAnnouncement = async (orgId, cohortId, announcementId) => {
  try {
    const ref = doc(db, "organizations", orgId, "cohorts", cohortId)
    const snap = await getDoc(ref)
    if (!snap.exists()) return { error: { message: "Cohort not found" } }
    const announcements = snap.data()?.announcements || []
    const next = announcements.filter((a) => a.id !== announcementId)
    await updateDoc(ref, { announcements: next, updatedAt: serverTimestamp() })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// attendance shape: { [eventId]: { [userId]: true|false } }
export const setAttendance = async (orgId, cohortId, eventId, userId, present) => {
  try {
    await updateDoc(doc(db, "organizations", orgId, "cohorts", cohortId), {
      [`attendance.${eventId}.${userId}`]: present,
      updatedAt: serverTimestamp(),
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}